import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowLeft, Check } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button } from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useCreateContract } from '@/api/contracts';
import {
  contractSchema, defaultValues, buildPayload, type ContractFormValues,
} from './contract-form';
import { CustomerSection } from './customer-section';
import { DatesSection } from './dates-section';
import { SettingsSection } from './config-sections/settings-section';
import { ProductsSection } from './products-section';
import { TotalSection } from './total-section';
import { AgreementSection } from './agreement-section';

/**
 * `/contracts/new` — single-page contract create form, a 1-1 rebuild of the
 * legacy datacentral-client-v4 `Contract/components/New`. Sections (customer →
 * dates → config → products → total → agreement) all bind one react-hook-form
 * over the full `ContractCreatePayload`; submit posts `POST /contracts/customers`.
 * The deep SLA/NDA builders and per-product-type pricing matrix are later phases.
 */
export default function ContractNewPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const create = useCreateContract();
  const [submitError, setSubmitError] = useState('');
  const [contactRoles, setContactRoles] = useState<string[]>([]);

  const [searchParams] = useSearchParams();

  const methods = useForm<ContractFormValues>({
    resolver: yupResolver(contractSchema),
    mode: 'onBlur',
    defaultValues: defaultValues(),
  });
  const customerId = useWatch({ control: methods.control, name: 'customer_id' });

  // Prefill from the deep-link URL (Sale Process "Create contract" links to
  // `/contracts/new?customer_id=&customer_journey_id=&contact_id=`). Setting
  // `customer_id` triggers the existing cascade (service-interestings + product
  // auto-append); `customer_journey_id` ties the contract back to the journey.
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (prefilledRef.current) return;
    prefilledRef.current = true;
    const setNum = (param: string, field: 'customer_id' | 'customer_contact_id' | 'customer_journey_id') => {
      const n = Number(searchParams.get(param));
      if (Number.isFinite(n) && n > 0) methods.setValue(field, n);
    };
    setNum('customer_id', 'customer_id');
    setNum('contact_id', 'customer_contact_id');
    setNum('customer_journey_id', 'customer_journey_id');
  }, [searchParams, methods]);

  const onSubmit = async (values: ContractFormValues) => {
    setSubmitError('');
    try {
      const res = await create.mutateAsync(buildPayload(values));
      const id = res?.result?.contract_id;
      toast.success('Contract created');
      navigate(id ? `/contracts/${id}` : '/contracts');
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to create contract. Please try again.';
      setSubmitError(message);
      toast.error('Failed to create contract', { description: message });
    }
  };

  return (
    <Container size="lg">
      <PageHeader
        title="New contract"
        description="Register a customer contract."
        actions={
          <Button variant="ghost" startIcon={<ArrowLeft size={15} />} onClick={() => navigate('/contracts')}>
            Back to contracts
          </Button>
        }
      />

      <FormProvider {...methods}>
        {/* `position: relative` contains the Select controls' hidden native
            <select> fallbacks (absolute, 1px) so they can't escape the scroll
            container and stretch the document height (phantom scroll void). */}
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          style={{ display: 'grid', gap: 'var(--vn-space-5)', position: 'relative' }}
        >
          <CustomerSection onRolesChange={setContactRoles} />
          <DatesSection />
          <SettingsSection customerId={customerId} />
          <ProductsSection customerId={customerId} />
          <TotalSection />
          <AgreementSection contactRoles={contactRoles} />

          {submitError ? <Alert tone="danger" title={submitError} /> : null}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-3)' }}>
            <Button
              type="submit"
              loading={create.isPending}
              disabled={create.isPending || !customerId}
              startIcon={<Check size={15} />}
            >
              Create contract
            </Button>
          </div>
        </form>
      </FormProvider>
    </Container>
  );
}
